import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { useNavigate } from "react-router-dom";
import { TableHeaderFilter } from "@lib/types/Api";
import { useEffect, useMemo, useState } from "react";
import { Invoice } from "@lib/types/Billing";
import { InvoiceDrawer } from "@lib/components/PageComponents/Invoice";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useIntl } from "react-intl";
import { useApi } from "@lib/hooks/APIHooks";
import { useInvoiceQueries } from "@lib/apiQueries";
import { SharedContextProps } from "@lib/types";
import useScreen from "@lib/hooks/useScreen";
import { useTypedSelector } from "@lib/redux";
import TableSummary from "./TableSummary";

// export default function InvoiceList() {
const SharedInvoices: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customTitle,
  customerId,
  withoutBreadcrumb,
  withoutContainer,
  pageIsActive = true,
  tableProps,
  height,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { queriesCacheClear } = useApi();
  const { isMobile } = useScreen();
  const { role } = useTypedSelector((state) => state.auth.user);

  const initialFilters = [
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer", "orders"],
    filter: initialFilters,
  });

  const invoiceListQueryKey = ["invoices"];
  const { invoicesQuery, deleteInvoiceMutation } = useInvoiceQueries({
    messages,
    queryKey: [...invoiceListQueryKey, JSON.stringify(params)],
    params,
    enabled: pageIsActive,
  });
  const { data, isLoading, isFetching } = invoicesQuery;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (isFetching) {
      queriesCacheClear({ search: "invoice-detail" });
    }
  }, [isFetching]);

  useEffect(() => {
    if (deleteInvoiceMutation.isSuccess) {
      queriesCacheClear({ search: "invoice" });
    }
  }, [deleteInvoiceMutation.isSuccess]);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selected, setSelected] = useState<Invoice>();

  useEffect(() => {
    if (!openDrawer) setSelected(null);
  }, [openDrawer]);

  const toggleDrawer = (status?: boolean) => {
    if (typeof status !== "undefined") setOpenDrawer(status);
    else setOpenDrawer(!openDrawer);
  };

  const handleSelectInvoice = (invoice: Invoice) => {
    setSelected(invoice);
    toggleDrawer(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<Invoice>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        handleSelectInvoice,
        navigate,
        deleteQuery: deleteInvoiceMutation,
        openDrawer,
        apiData,
        params,
        messages,
        columnsConfig: tableProps?.columnsConfig,
        isMobile,
        userRole: role,
      }),
    [apiData, deleteInvoiceMutation, openDrawer, handleSelectInvoice, params],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["customerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params]);

  const title = customTitle || (messages["invoice.list"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      showBreadcrumb={!withoutBreadcrumb}
      noContainer={!!withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={invoicesQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggleDrawer(true),
            disabled: isLoading,
            loading: isLoading,
          }}
          tableProps={tableProps}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          summary={() => <TableSummary data={data?.data} />}
          showSummary={true}
          {...tableProps}
        />
      </AppsContent>

      <InvoiceDrawer
        toggleDrawer={toggleDrawer}
        openDrawer={openDrawer}
        invoice={selected}
        setSelectedInvoice={handleSelectInvoice}
        invoiceListQueryKey={invoiceListQueryKey}
      />
    </AppsContainer>
  );
};

export default SharedInvoices;
